import * as z from 'zod';

// The branches ("О заведении") screen lists every branch a merchant owns, each
// as an address row beside a map pin. The view model is deliberately narrower
// than `v1Restaurant`: this screen renders an address and a location, and the
// editor re-reads the full branch by id when it opens.

/**
 * A geo point in the backend's `{lat, long}` convention, flipped to
 * `[long, lat]` for ymaps at the widget boundary (see `entities/venue`'s
 * `to-lng-lat`, which this screen reuses).
 */
export const branchLocationSchema = z.object({
  lat: z.number(),
  long: z.number(),
});
export type BranchLocation = z.infer<typeof branchLocationSchema>;

/**
 * One branch as the list renders it. `location` is optional: a branch whose
 * address carries no coordinates gets a row but no map pin, rather than a pin
 * dropped at (0, 0) in the Gulf of Guinea.
 */
export const branchSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Single-line street address; empty when the merchant hasn't set one. */
  address: z.string(),
  acceptingOrdersNow: z.boolean().optional(),
  pickupAvailable: z.boolean().optional(),
  isPopular: z.boolean().optional(),
  location: branchLocationSchema.optional(),
  /**
   * Moderation state, badged on the card and narrowed by the screen's status
   * select.
   *
   * The UI vocabulary is spelled out here rather than imported from another
   * entity, because FSD forbids one entity importing another.
   *
   * Read from `v1Restaurant.moderation_state`.
   */
  moderation: z
    .enum(['unspecified', 'draft', 'pending', 'approved', 'rejected'])
    .optional(),
  /** Why moderation sent the branch back (`v1Restaurant.rejection_reason`). */
  rejectionReason: z.string().optional(),
  /**
   * Showcase ("Витрина") detail, carried straight from the published list row
   * so the view-mode drawer (Figma 1632:39021) renders real photos, pricing
   * and taxonomy without a second request. All optional: rows pre-dating the
   * detail mapping, and branches without the data, simply omit them and the
   * drawer falls back to its placeholders.
   */
  /** Square brand logo (`v1Restaurant.icon`), rendered cropped to a circle. */
  iconUrl: z.string().optional(),
  /** Wide header photo (`v1Restaurant.background_image`). */
  backgroundUrl: z.string().optional(),
  /** Minimum basket total for free delivery (`0` = no offer). */
  freeDeliveryThreshold: z.number().int().nonnegative().optional(),
  /** Where the branch's menu comes from (`v1Restaurant.menu_source`). */
  menuSource: z.string().optional(),
  /** Locale-resolved blurb (`v1Restaurant.description`). */
  description: z.string().optional(),
  /** Discovery-chip tag ids (`v1Restaurant.restaurant_category_ids`). */
  categoryIds: z.array(z.string()).optional(),
  /**
   * Default daily window (`v1Restaurant.working_hours`). Seeds the drawer's
   * schedule rows when the per-day schedule hasn't loaded yet.
   */
  workingHours: z.object({ open: z.string(), close: z.string() }).optional(),
});
export type Branch = z.infer<typeof branchSchema>;

/** The moderation states the branches screen filters by, in the design's order. */
export const BRANCH_MODERATIONS = [
  'unspecified',
  'draft',
  'pending',
  'approved',
  'rejected',
] as const;
export type BranchModeration = (typeof BRANCH_MODERATIONS)[number];

/** The merchant heading the list: its display name and round logo. */
export const branchOwnerSchema = z.object({
  name: z.string(),
  logoUrl: z.string(),
  menuSource: z.string().optional(),
});
export type BranchOwner = z.infer<typeof branchOwnerSchema>;
